import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Tile from "components/Tile";
import TileBody from "components/TileBody";
import ActionLink from "components/ActionLink";
import { modal, confirm } from "containers/App/_actions";
import { undoChanges, syncDirtyBudgets } from "containers/Budget/_actions";

export default function MyPendingSaves({ pendingSaves }) {
    const dispatch = useDispatch();
    const online = useSelector((state) => state.app.online);

    const _retryAll = (e) => {
        e.preventDefault();
        dispatch(syncDirtyBudgets());
    };

    const _copy = (offlineBudget, isSyncing) => (e) => {
        e.preventDefault();
        if (!isSyncing) {
            const viewModel = {
                farm: offlineBudget.farm,
                analysisId: offlineBudget.budget.id,
                initialCreationType: "Copy",
            };
            dispatch(modal("CreateAnalysisModal", viewModel));
        }
    };

    const _retryOne = (id, isSyncing) => (e) => {
        e.preventDefault();
        if (!isSyncing) {
            dispatch(syncDirtyBudgets(id));
        }
    };

    const _undoChanges = (offlineBudget, isSyncing) => (e) => {
        e.preventDefault();
        if (!isSyncing) {
            dispatch(confirm(`Are you sure you want to undo the changes for analysis, '${offlineBudget.budget.name}'?`, () => dispatch(undoChanges(offlineBudget.budget))));
        }
    };

    const _reason = (offlineBudget) => {
        let message = "";
        if (offlineBudget.error) {
            switch (offlineBudget.error.status) {
                case 400:
                    message += "There is something wrong with the data for this analysis. Contact support or log out to clear reset the analysis. This will remove all unsaved changes.";
                    break;
                case 401:
                    message += "Unauthorised access to this analysis.";
                    break;
                case 402:
                    message += "The subscription has expired for this farm. Select the farm and pay the subscription.";
                    break;
                case 426:
                    message += "Analysis modified by another user. Your version is out of date. You can either undo your changes or copy your analysis to a new one and try again.";
                    break;
                default:
                    message += "An error has occurred while saving this analysis. Please try again and if problems perists contact support.";
                    break;
            }
            message += ` (Code: ${offlineBudget.error.status}${offlineBudget.error.requestId ? ` RequestId: ${offlineBudget.error.requestId}` : ""})`;
        } else if (offlineBudget.isDirty) {
            message = "Pending offline sync";
        }

        //const hasSyncErrors = offlineBudget && offlineBudget.error;
        //return <span className="IconText"><i className={`icon icon-sync ${hasSyncErrors ? 'u-textError' : 'u-textWarning'}`}></i> {message}</span>;
        return message;
    };

    const actions = (
        <ActionLink className="Tile-head-close IconLink--sync u-link" onClick={_retryAll}>
            Retry all
        </ActionLink>
    );

    return (
        <Tile title="Pending saves" actions={online ? actions : undefined}>
            <TileBody>
                <div className="Table">
                    <table>
                        <thead>
                            <tr>
                                <th data-width="20">Farm name</th>
                                <th data-width="30">Address</th>
                                <th data-width="20">Analysis</th>
                                <th>Status</th>
                                <th className="th-shrink"></th>
                                <th className="th-shrink"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingSaves.map((offlineBudget) => {
                                const isSyncing = offlineBudget && offlineBudget.isSyncing;
                                const hasSyncErrors = offlineBudget && offlineBudget.error;
                                const isConcurrencyError = hasSyncErrors && offlineBudget.error.status === 426;
                                return (
                                    <tr key={offlineBudget.id} className="">
                                        <td>
                                            <Link id={`view-analysis-${offlineBudget.id}`} to={`/app/farm/${offlineBudget.farmId}/analysis/${offlineBudget.id}`}>
                                                <b>{offlineBudget.farm.name}</b>
                                            </Link>
                                        </td>
                                        <td>{offlineBudget.farm.address}</td>
                                        <td>{offlineBudget.budget.name}</td>
                                        <td>{_reason(offlineBudget)}</td>
                                        <td>
                                            <i className={`icon icon-sync ${isSyncing ? "u-textMidGrey" : hasSyncErrors ? "u-textError" : "u-textWarning"}`}></i>
                                        </td>
                                        <td>
                                            <div className="u-flex">
                                                <ActionLink className={`IconLink--cross-circle u-mr-sm ${isSyncing ? "u-textMidGrey" : ""}`} disabled={isSyncing} id={`undo-${offlineBudget.id}`} onClick={_undoChanges(offlineBudget, isSyncing)}>
                                                    Undo
                                                </ActionLink>
                                                {isConcurrencyError && (
                                                    <ActionLink className={`IconLink--copy ${isSyncing ? "u-textMidGrey" : ""}`} disabled={isSyncing} id={`copy-${offlineBudget.id}`} onClick={_copy(offlineBudget, isSyncing)}>
                                                        Copy
                                                    </ActionLink>
                                                )}
                                                {online && !isConcurrencyError && (
                                                    <ActionLink className={`u-ml-xs IconLink--sync ${isSyncing ? "u-textMidGrey" : ""}`} disabled={isSyncing} id={`retry-${offlineBudget.id}`} onClick={_retryOne(offlineBudget.id, isSyncing)}>
                                                        Retry
                                                    </ActionLink>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </TileBody>
        </Tile>
    );
}
